import { ref } from 'vue';
import type { Router } from 'vue-router';

const open = ref(false);
let failedPath: string | null = null;

/** Ошибки загрузки JS/CSS в Chrome, Firefox, Safari и Vite. */
export function isModuleLoadError(error: unknown): boolean {
  const message = typeof error === 'string'
    ? error
    : error && typeof error === 'object' && 'message' in error
      ? String(error.message)
      : '';
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS for/i.test(message);
}

/**
 * Открытая вкладка может запросить файл предыдущей сборки или потерять сеть.
 * Не перезагружаем её автоматически: на текущем экране может быть черновик.
 */
export function reportModuleLoadError(error: unknown, path?: string): boolean {
  if (!isModuleLoadError(error)) return false;
  failedPath = path ?? failedPath ?? `${location.pathname}${location.search}${location.hash}`;
  open.value = true;
  return true;
}

function dismiss(): void {
  open.value = false;
  failedPath = null;
}

function reload(): void {
  const target = new URL(failedPath ?? location.href, location.origin);
  // Путь приходит из роутера; полная навигация остаётся на том же сайте.
  if (target.origin !== location.origin) return;
  location.assign(target.href);
}

export function useModuleRecovery() {
  return { open, dismiss, reload };
}

export function installModuleRecovery(router: Pick<Router, 'onError'>): () => void {
  const onPreloadError = (event: Event) => {
    reportModuleLoadError((event as Event & { payload?: unknown }).payload);
    // Не подавляем отказ: иначе роутер может принять undefined за компонент
    // и размонтировать текущую страницу вместо отмены перехода.
  };
  window.addEventListener('vite:preloadError', onPreloadError);
  const stopRouter = router.onError((error, to) => {
    if (!reportModuleLoadError(error, to.fullPath)) console.error(error);
  });
  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
    stopRouter();
  };
}
